import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { BodyM } from '@sorare/core/src/atoms/typography';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { Option } from '@sorare/core/src/components/search/Option';
import type {
  GallerySortName,
  PrintablePlayerSortName,
} from '@sorare/core/src/contexts/config';
import { useGQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { useBackendI18nSortByItems } from '@sorare/core/src/hooks/search/useI18nSortByItems';
import { useMarketplaceLifecycle } from '@sorare/core/src/hooks/useMarketplaceLifecycle';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';

type Props = {
  sorts: (GallerySortName | PrintablePlayerSortName)[];
};

export const useBackendSortWidget = ({ sorts }: Props): FilterWidget => {
  const ctx = useGQLSearchContext()!;
  const { updateSort } = useMarketplaceLifecycle();
  const sortItems = useBackendI18nSortByItems(sorts as GallerySortName[]);

  const currentSort = ctx.sorts[0] ?? sorts[0];

  const sortWidget: FilterWidget = useMemo(
    () => ({
      key: 'sort',
      type: 'sort',
      title: (
        <BodyM bold>
          <FormattedMessage {...filters.sortBy} />
        </BodyM>
      ),
      Component: ({ visible }) => {
        if (!visible) return <FilterSection />;

        return (
          <div className="FilterWidget visible">
            {sortItems.map(sortItem => (
              <div key={sortItem.value}>
                <Option
                  active={currentSort === sortItem.value}
                  onClick={() => {
                    ctx.setSearchParams(prev => {
                      prev.delete('p');
                      prev.set('s', sortItem.value);
                      return prev;
                    });
                    updateSort(sortItem.index);
                  }}
                  variant="radio"
                >
                  {sortItem.label}
                </Option>
              </div>
            ))}
          </div>
        );
      },
    }),
    [currentSort, ctx, sortItems, updateSort]
  );

  return sortWidget;
};
